/**
 * Find a player by nickname or email and hand back their id.
 *
 * The invoice form used to ask for a raw "player id", but no admin screen ever
 * shows one: the Players table lists nickname and email only, so an invoice could
 * not be raised at all without reading the database. The picker searches the same
 * `/admin/players` endpoint the Players tab uses and keeps the id to itself.
 */
import { el, clear, toast } from '../../dom';
import { adminApi, type AdminPlayer } from '../../game-api';
import { btn, input, toastError } from './ui';

export interface PickedPlayer {
  id: string;
  nickname: string | null;
  email: string;
}

export interface PlayerPicker {
  wrap: HTMLElement;
  /** The chosen player's id, or null when none is chosen yet. */
  value(): string | null;
  set(p: PickedPlayer): void;
}

/** How the chosen player reads in the field. */
export function playerLabel(p: PickedPlayer): string {
  return p.nickname ? `${p.nickname} (${p.email})` : p.email;
}

export interface PlayerPickerOptions {
  /**
   * Offer "+ CREATE NEW": an e-mail and an optional username make an
   * invoice-only account through core's create-for-invoice (e-mail keyed — an
   * e-mail already on file returns that player instead). The server flags it
   * admin-created, so its invoices are never e-mailed.
   */
  allowCreate?: boolean;
}

export function playerPicker(onPick: (p: PickedPlayer) => void, opts: PlayerPickerOptions = {}): PlayerPicker {
  const field = input('Search nickname or email…');
  field.autocomplete = 'off';
  // In the flow, not absolutely positioned: the invoice form sits in a modal whose
  // body scrolls, and a floating list there is clipped by that scroll box.
  const list = el('div', 'mt-1 rounded-lg bg-surface-2 border border-border max-h-56 overflow-y-auto hidden');
  const wrap = el('div', '', {}, field, list);
  if (opts.allowCreate) wrap.appendChild(createNew());
  let chosen: PickedPlayer | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let seq = 0;

  const hide = () => list.classList.add('hidden');
  const show = (nodes: HTMLElement[]) => {
    clear(list);
    for (const n of nodes) list.appendChild(n);
    list.classList.toggle('hidden', nodes.length === 0);
  };

  function set(p: PickedPlayer) {
    chosen = p;
    field.value = playerLabel(p);
    hide();
    onPick(p);
  }

  field.addEventListener('input', () => {
    chosen = null;
    clearTimeout(timer);
    const q = field.value.trim();
    if (q.length < 2) { hide(); return; }
    const mine = ++seq;
    timer = setTimeout(() => {
      adminApi.players({ query: q, limit: 8 }).then((page) => {
        if (mine !== seq) return; // a newer search is in flight
        const items: AdminPlayer[] = page.items ?? [];
        show(items.length
          ? items.map((p) => el('button',
            'block w-full text-left px-3 py-2 text-sm hover:bg-surface min-h-[44px]',
            { type: 'button', onClick: () => set({ id: p.id, nickname: p.nickname ?? p.username ?? null, email: p.email }) },
            el('div', '', {}, p.nickname ?? p.username ?? '—'),
            el('div', 'text-[11px] text-text-dim', {}, p.email)))
          : [el('div', 'px-3 py-2 text-xs text-text-dim', {}, 'No player matches.')]);
      }).catch(() => show([el('div', 'px-3 py-2 text-xs text-error', {}, 'Search failed.')]));
    }, 250);
  });
  field.addEventListener('blur', () => setTimeout(hide, 150));

  /** "+ CREATE NEW" ⇄ "< SEARCH": e-mail + optional username → create-for-invoice. */
  function createNew(): HTMLElement {
    const email = input('E-mail (required)', '', 'email');
    const username = input('Username (optional — defaults to the e-mail name)');
    const form = el('div', 'mt-2 flex flex-col gap-2 hidden', {}, email, username);
    let busy = false;
    const create = btn('Create player', async () => {
      const e = email.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) { toast('Enter a valid e-mail', 'error'); return; }
      if (busy) return;
      busy = true;
      try {
        const u = await adminApi.createUserForInvoice({ email: e, username: username.value.trim() || undefined });
        set({ id: u.id, nickname: u.username ?? null, email: u.email });
        email.value = ''; username.value = '';
        toggle(false);
        toast(`Player ${u.username ?? u.email} ready`);
      } catch (err) {
        toastError(err, 'Could not create the player');
      } finally { busy = false; }
    }, 'primary');
    form.appendChild(create);
    const toggleBtn = btn('+ CREATE NEW', () => toggle(form.classList.contains('hidden')));
    const toggle = (open: boolean) => {
      form.classList.toggle('hidden', !open);
      field.classList.toggle('hidden', open);
      toggleBtn.textContent = open ? '< SEARCH' : '+ CREATE NEW';
      if (open) hide();
    };
    return el('div', 'mt-1', {}, toggleBtn, form);
  }

  return { wrap, value: () => chosen?.id ?? null, set };
}
